import React, { useEffect, useState } from "react";
import { Form, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GameFeaturingPromoState } from "@/const";
import GameFeaturingPromoStateDisplay from "@components/Displays/GameFeaturingPromoStateDisplay";
import { DevForm } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { Awesome, DevModal, LocaleDropdown } from "@components/Helpers";
import { CommonCard, ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

export default function FeaturingPromo(props) {
    const params = useParams();
    if (params.id) {
        props = { ...props, id: params.id };
        return <Edit {...props} />;
    }
    return <List {...props} />;
}

function List({ gameId, header }) {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:game:featuring_promo:list", { game_id: gameId }));
    const { setDelData } = useAppContext();

    if (!apiData) return;

    const headerTools = (
        <Link
            to={url_reverse("games:featuring:promo:edit", { game_id: gameId, promo_id: "add" })}
            className="btn btn-sm btn-dark"
        >
            <Awesome type="plus-circle" /> {t("Добавить")}
        </Link>
    );

    return (
        <ListingCard header={header} headerTools={headerTools}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Заголовок")}</th>
                        <th>{t("Дата начала")}</th>
                        <th>{t("Дата окончания")}</th>
                        <th>{t("Статус")}</th>
                        <th style={{ width: "6%" }}></th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((o, i) => {
                        return (
                            <tr key={`object_list${i}`}>
                                <td>
                                    <Link
                                        to={url_reverse("games:featuring:promo:edit", {
                                            game_id: gameId,
                                            promo_id: o.id,
                                        })}
                                    >
                                        {o.__model_locale_object.name}
                                    </Link>
                                </td>
                                <td>{o.date_start}</td>
                                <td>{o.date_stop}</td>
                                <td>
                                    <GameFeaturingPromoStateDisplay state={o.state} />
                                </td>
                                <td className="dmg-table-row-tools">
                                    {[GameFeaturingPromoState.NEW, GameFeaturingPromoState.REJECTED].includes(
                                        o.state
                                    ) && (
                                        <Awesome
                                            type="trash-o"
                                            color="red"
                                            onClick={() =>
                                                setDelData({
                                                    url: url_reverse("api:game:featuring_promo:delete", {
                                                        game_id: gameId,
                                                        promo_id: o.id,
                                                    }),
                                                })
                                            }
                                        />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </ListingCard>
    );
}

function Edit({ id, gameId, locl, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(
        url_reverse("api:game:featuring_promo:edit", { game_id: gameId, promo_id: id })
    );
    const onFormSubmit = useSubmit();
    const [curLoc, setCurLoc] = useState(locl);
    const [postModerPublish, setPostModerPublish] = useState(false);
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");

    useEffect(() => {
        const data = apiData?.forms_data?.main.data;
        if (data) {
            const newApiData = { ...apiData };
            newApiData.forms_data.main.fields.url_on_page.widget.is_hidden = !data.post_moderation;
            newApiData.forms_data.main.fields.gc_url.widget.is_hidden = !data.post_moderation;
            newApiData.forms_data.main.fields.gc_mt_link_id.widget.is_hidden = !data.post_moderation;
            setApiData(newApiData);
        }
    }, [JSON.stringify(apiData)]);

    if (!apiData) return;

    const fieldProps = { apiData, setApiData };

    const handleFormSubmit = (ev) => {
        onFormSubmit(ev, { backUrl: url_reverse("games:featuring:promo:list", { game_id: gameId }) });
    };

    const closeModal = () => {
        setPostModerPublish(false);
    };

    const headerTools = apiData.active_locales && (
        <LocaleDropdown
            curLoc={curLoc}
            setCurLoc={setCurLoc}
            active_locales={apiData.active_locales}
            base_locale={gameCommonInfo.game.base_locale}
            forms_data={apiData.forms_data}
        />
    );

    return (
        <CommonCard header={header} headerTools={headerTools}>
            <p>{t("Для размещения акции на мир заполните, пожалуйста, как минимум английскую локализацию")}</p>

            <Form onSubmit={handleFormSubmit}>
                <DevForm.Field {...fieldProps} prefix="main" name="date_start" />
                <DevForm.Field {...fieldProps} prefix="main" name="date_stop" />
                <DevForm.Field {...fieldProps} prefix="main" name="geo_location" />
                <hr />
                {curLoc && apiData.forms_data && (
                    <RenderForm prefix={curLoc} apiData={apiData} setApiData={setApiData} bareForm={true} />
                )}
                <DevForm.Field {...fieldProps} prefix="main" name="developer_comment" />
                <DevForm.Field {...fieldProps} prefix="main" name="post_moderation" />
                <DevForm.Field {...fieldProps} prefix="main" name="url_on_page" />
                <DevForm.Field {...fieldProps} prefix="main" name="gc_url" />
                <DevForm.Field {...fieldProps} prefix="main" name="gc_mt_link_id" />
                {(apiData.force_allow_basic_promotion_button ||
                    !apiData.object?.state ||
                    [GameFeaturingPromoState.NEW, GameFeaturingPromoState.REJECTED].includes(apiData.object.state)) && (
                    <>
                        <DevForm.Button variant="primary" type="submit" name="save">
                            {t("Сохранить")}
                        </DevForm.Button>{" "}
                        {apiData.forms_data.main.data.post_moderation ? (
                            <DevForm.Button variant="warning" name="publish" onClick={() => setPostModerPublish(true)}>
                                {t("Опубликовать")}
                            </DevForm.Button>
                        ) : (
                            <DevForm.Button variant="warning" type="submit" name="action" value="to_moderate">
                                {t("Отправить на проверку")}
                            </DevForm.Button>
                        )}
                    </>
                )}
                {apiData.object?.state === GameFeaturingPromoState.MODERATE && (
                    <DevForm.Button variant="warning" type="submit" name="action" value="to_new">
                        {t("Снять с проверки")}
                    </DevForm.Button>
                )}
                {[GameFeaturingPromoState.PUBLISHED, GameFeaturingPromoState.POSTMODERATE].includes(
                    apiData.object?.state
                ) && (
                    <DevForm.Button variant="warning" type="submit" name="action" value="to_finished">
                        {t("Снять с публикации")}
                    </DevForm.Button>
                )}
            </Form>
            <DevModal
                show={postModerPublish}
                onHide={closeModal}
                header={t("Подтвердите, что акция не является рекламной")}
                body={t(
                    "Вы подтверждаете, что эта акция не является рекламной и несёте ответственность за её публикацию?"
                )}
                footer={
                    <>
                        <DevForm.Button variant="secondary" name="cancel" onClick={closeModal}>
                            {t("Отмена")}
                        </DevForm.Button>
                        <DevForm.Button
                            variant="primary"
                            name="action"
                            value="to_postmoderate"
                            onClick={(ev) => {
                                handleFormSubmit(ev);
                                closeModal();
                            }}
                        >
                            {t("Подтвердить")}
                        </DevForm.Button>
                    </>
                }
            />
        </CommonCard>
    );
}
